import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonModal,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { apiFetch } from "../api/client.js";
import { currentWeekRange, dateEnd, dateStart, formatTime } from "../utils/date.js";

const SEARCH_DEBOUNCE_MS = 300;

function resultDate(value) {
  return new Intl.DateTimeFormat("en", {
    weekday: "long",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

export default function ScheduleSearchModal({ isOpen, onClose, onSelect }) {
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(currentWeekRange);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const normalizedSearch = search.trim();
  const hasValidRange = Boolean(range.startDate && range.endDate && range.startDate <= range.endDate);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSearch("");
    setRange(currentWeekRange());
    setResults([]);
    setError("");
    setLoading(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || normalizedSearch.length < 3 || !hasValidRange) {
      setResults([]);
      setLoading(false);
      setError("");

      return undefined;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          search: normalizedSearch,
          from: dateStart(range.startDate).toISOString(),
          to: dateEnd(range.endDate).toISOString(),
        });
        const nextResults = await apiFetch(`/schedule?${params.toString()}`, {
          signal: controller.signal,
          showGlobalLoading: false,
        });

        setResults(nextResults);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(requestError.message);
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [hasValidRange, isOpen, normalizedSearch, range.endDate, range.startDate]);

  function selectResult(occurrence) {
    onSelect(occurrence);
    onClose();
  }

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="schedule-search-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>Find a Job</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} aria-label="Close schedule search">
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="schedule-search-content">
          <IonSearchbar
            value={search}
            onIonInput={(event) => setSearch(event.detail.value || "")}
            placeholder="Search client name"
            aria-label="Search client name"
            inputMode="search"
            enterkeyhint="search"
          />
          <div className="date-range-fields schedule-search-dates">
            <IonInput
              className="metora-input"
              label="From"
              labelPlacement="stacked"
              type="date"
              value={range.startDate}
              onIonInput={(event) => setRange((current) => ({
                ...current,
                startDate: event.detail.value || "",
              }))}
            />
            <IonInput
              className="metora-input"
              label="To"
              labelPlacement="stacked"
              type="date"
              value={range.endDate}
              onIonInput={(event) => setRange((current) => ({
                ...current,
                endDate: event.detail.value || "",
              }))}
            />
          </div>

          <div className="schedule-search-results" aria-live="polite">
            {normalizedSearch.length < 3 && (
              <p className="schedule-search-message">Type at least 3 characters to search.</p>
            )}
            {!hasValidRange && (
              <p className="schedule-search-message schedule-search-error">
                The end date must be on or after the start date.
              </p>
            )}
            {loading && (
              <div className="schedule-search-loading">
                <IonSpinner name="crescent" />
                <span>Searching…</span>
              </div>
            )}
            {!loading && error && (
              <p className="schedule-search-message schedule-search-error">{error}</p>
            )}
            {!loading && !error && normalizedSearch.length >= 3 && hasValidRange && results.length === 0 && (
              <p className="schedule-search-message">No matching occurrences in this date range.</p>
            )}
            {!loading && results.map((occurrence) => (
              <button
                className="schedule-search-result"
                type="button"
                key={occurrence.id}
                onClick={() => selectResult(occurrence)}
              >
                <span className="schedule-search-result-copy">
                  <strong>{occurrence.client?.name || "Client"}</strong>
                  {occurrence.internalLabel && <small>{occurrence.internalLabel}</small>}
                </span>
                <span className="schedule-search-result-date">
                  <strong>{resultDate(occurrence.scheduledStart)}</strong>
                  <small>{formatTime(occurrence.scheduledStart)}</small>
                </span>
              </button>
            ))}
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
}
